import {chain} from 'lodash';
import {createSelector} from 'reselect';

import {ApplicationEntryPointTypesEnum} from '../../../models/api/applicationEntryPointModel';
import {AppState} from '../../appState';
import {AsyncStatus, ResourceTableStatusesEnum} from '../../appTypes';

/** Get a map of all applications by id. */
export function getApplications(state: AppState) {
  return state.data.applications.byId;
}

export const getFlatApplications = createSelector([getApplications], (applications) =>
  chain(applications).values().compact().value(),
);

/*
 * Entry points.
 */

const getEntryPoints = createSelector(getFlatApplications, (applications) =>
  chain(applications)
    .flatMap((application) => chain(application.entryPoints).compact().value())
    .sortBy((e) => e && e.name)
    .compact()
    .value(),
);

export const getInboxSidePanelEntryPoints = createSelector(getEntryPoints, (entryPoints) =>
  chain(entryPoints)
    .filter((e) => e.type === ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL)
    .compact()
    .value(),
);

/*
 * Application Status Selectors.
 */

export function getApplicationsAsyncStatus(state: AppState): AsyncStatus {
  return state.data.applications;
}

export const getIsApplicationsLoading = createSelector(
  getApplicationsAsyncStatus,
  (status) =>
    status.status === ResourceTableStatusesEnum.INITIAL ||
    status.status === ResourceTableStatusesEnum.LOADING,
);

export const getShouldFetchApplications = createSelector(getApplicationsAsyncStatus, ({isStale, status}) => {
  // If the store is marked as stale, we should refresh.
  if (isStale) {
    return true;
  }
  // If the status is either failed or initial state, we should fetch.
  if (status === ResourceTableStatusesEnum.INITIAL || status === ResourceTableStatusesEnum.FAILED) {
    return true;
  }
  // We can just rely on the redux state currently.
  return false;
});
